import { useCallback, useEffect, useState } from "react";
import {
  listarFormaciones,
} from "../../../../services/formacionesService";
import { normalizeFormacion } from "../adapters/formacionesAdapter";
import { showToast } from "../../../../utils/showToast";

const initialFilters = {
  search: "",
  estado: "all",
  activo: "all",
  promocion: "all",
};

const initialResumen = {
  total: 0,
  inscritos: 0,
  cursando: 0,
  egresados: 0,
  activos: 0,
  inactivos: 0,
};

export default function useFormacionesList() {
  const [formacionesPaginadas, setFormacionesPaginadas] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtros, setFiltros] = useState(initialFilters);
  const [searchDebounced, setSearchDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [resumen, setResumen] = useState(initialResumen);

  // Debounce del texto de busqueda para no disparar una consulta por cada tecla
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(filtros.search.trim()), 350);
    return () => clearTimeout(timer);
  }, [filtros.search]);

  useEffect(() => {
    setPage(1);
  }, [searchDebounced, filtros.estado, filtros.activo, filtros.promocion]);

  const fetchFormaciones = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listarFormaciones({
        pagina: page,
        porPagina: pageSize,
        search: searchDebounced || null,
        estado: filtros.estado,
        activo: filtros.activo,
        promocion: filtros.promocion,
      });
      const rows = Array.isArray(response?.data) ? response.data : [];
      setFormacionesPaginadas(rows.map(normalizeFormacion));
      setTotal(Number(response?.total) || 0);
      setResumen({ ...initialResumen, ...(response?.resumen || {}) });
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudieron obtener las formaciones.";
      setError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchDebounced, filtros.estado, filtros.activo, filtros.promocion]);

  useEffect(() => {
    fetchFormaciones();
  }, [fetchFormaciones]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const goToPage = useCallback(
    (nextPage) => {
      const safe = Math.min(totalPages, Math.max(1, nextPage));
      setPage(safe);
    },
    [totalPages]
  );

  const upsertFormacion = useCallback((nueva) => {
    if (!nueva?.id) return;
    setFormacionesPaginadas((prev) => {
      const exists = prev.some((item) => String(item.id) === String(nueva.id));
      if (!exists) return prev;
      return prev.map((item) =>
        String(item.id) === String(nueva.id) ? normalizeFormacion({ ...item, ...nueva }) : item
      );
    });
    setLastUpdated(new Date());
  }, []);

  const removeFormacion = useCallback((id) => {
    setFormacionesPaginadas((prev) => prev.filter((item) => item.id !== id));
    setLastUpdated(new Date());
  }, []);

  const seleccionar = useCallback((formacion) => {
    if (!formacion) {
      setSeleccionado(null);
      return;
    }
    setSeleccionado(normalizeFormacion(formacion));
  }, []);

  const limpiarFiltros = useCallback(() => setFiltros(initialFilters), []);

  return {
    seleccionado,
    setSeleccionado,
    loading,
    error,
    resumen,
    filtros,
    setFiltros,
    limpiarFiltros,
    lastUpdated,
    page,
    pageSize,
    setPageSize,
    totalPages,
    total,
    goToPage,
    formacionesPaginadas,
    reload: fetchFormaciones,
    upsertFormacion,
    removeFormacion,
    seleccionar,
  };
}
